import {
  formatDateInAppTimeZone,
  formatDateTimeInAppTimeZone,
  formatTimeInAppTimeZone,
  formatTodayWithWeekdayInAppTimeZone,
} from "./appDateTime";

/**
 * Date and time formatting utilities.
 * Standardized formats per docs_local/ui-consistency/REFINE_PLAN.md.
 *
 * Summary of formats:
 *   formatTodayWithWeekday() -> "Thursday, June 18, 2026"   (page title subtitles)
 *   formatDateTime(date)     -> "Jun 18, 2026 2:30 PM"      (table date+time cells)
 *   formatDate(date)         -> "06/18/2026"                 (table date-only cells, forms)
 *   formatTime(date)         -> "2:30 PM"                    (time-only display)
 *   formatDateUTC(date)      -> "06/18/2026"                 (DOB — UTC timezone)
 */

/**
 * Returns today's date as a full-weekday, long-month string.
 * Used in page title subtitle areas.
 * Example output: "Thursday, June 18, 2026"
 */
export const formatTodayWithWeekday = () => {
  return formatTodayWithWeekdayInAppTimeZone();
};

/**
 * Formats a date+time value for table cells.
 * Short month, no seconds, 12-hour clock.
 * Example output: "Jun 18, 2026 2:30 PM"
 *
 * @param {string|Date} date
 * @returns {string}
 */
export const formatDateTime = (date) => {
  try {
    return formatDateTimeInAppTimeZone(date);
  } catch (error) {
    console.log(error);
  }
  return "";
};

/**
 * Formats a date-only value for table cells and forms.
 * Zero-padded MM/DD/YYYY.
 * Example output: "06/18/2026"
 *
 * @param {string|Date} date
 * @returns {string}
 */
export const formatDate = (date) => {
  try {
    return formatDateInAppTimeZone(date);
  } catch (error) {
    console.log(error);
  }
  return "";
};

/**
 * Formats a time-only value.
 * 12-hour clock, no seconds.
 * Example output: "2:30 PM"
 *
 * @param {string|Date} date
 * @returns {string}
 */
export const formatTime = (date) => {
  try {
    return formatTimeInAppTimeZone(date);
  } catch (error) {
    console.log(error);
    return "";
  }
};

/**
 * Formats a date-only value using UTC timezone.
 * Used for Date of Birth display to avoid timezone-shift off-by-one errors.
 * Zero-padded MM/DD/YYYY.
 * Example output: "06/18/2026"
 *
 * @param {string|Date} date
 * @returns {string}
 */
export const formatDateUTC = (date) => {
  try {
    if (!date) return "";
    const parsed = new Date(date);
    if (Number.isNaN(parsed.getTime())) return "";
    return parsed.toISOString().split("T")[0]; // YYYY-MM-DD in UTC
  } catch (error) {
    console.log(error);
  }
  return "";
};
